import { sportsnewDisplayOddGroupEvidence } from '../../../_context/_helpers/sportsnewListPatch.js';

// Presentation-only helper in the existing Sports/_helpers responsibility.
// CSS/React presentation generators do not support this pure helper directory.
// This describes committed public values for a separate retained-packet check;
// it never grants betting authority or declares a delivery/render ACK.
const PREFIX = 'data-sportsnew-odds-';
const SLOTS = new Set(['home', 'draw', 'away']);
const publicId = value => {
  if (typeof value !== 'number' && !(typeof value === 'string' && /^\d{1,16}$/.test(value))) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? String(id) : null;
};
const timestamp = value => {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value <= 0 || value > 8640000000000000) return null;
  return new Date(value).toISOString();
};
const publicPrice = value => {
  if (value === null) return 'null';
  if (typeof value !== 'number' && !(typeof value === 'string' && /^\d{1,8}(?:\.\d{1,8})?$/.test(value))) return null;
  const price = Number(value);
  return Number.isFinite(price) && price > 0 && price <= 100000000 ? String(price) : null;
};
const derivedAvailability = odd => {
  if (odd.marketLocked === true || odd.marketState != null) return 'parent-market';
  const reasons = [odd.reason, odd.lockReason].filter(value => typeof value === 'string');
  if (reasons.some(reason => /MEMBERSHIP_REFRESH_PENDING/i.test(reason))) return 'membership-hold';
  if (reasons.some(reason => /POLICY_|MANUAL|COMMAND|OPERATOR|DERIVED|RECOVERY|UNVERIFIED/i.test(reason))) return 'derived';
  return null;
};
const groupProps = (group, evidence, unavailable) => {
  const props = { [`${PREFIX}${group}-evidence`]: unavailable ?? 'missing' };
  if (unavailable) return props;
  if (!evidence || evidence.origin !== 'ws') {
    props[`${PREFIX}${group}-evidence`] = evidence ? 'non-ws' : 'missing';
    return props;
  }
  if (evidence.unverifiedRestHold) {
    props[`${PREFIX}${group}-evidence`] = 'rest-hold';
    return props;
  }
  const order = evidence.order;
  // Context's list order stores the public 24-hex filter hash, not a socket ID.
  // Detail/selected rooms and invalid/missing connection generations are refused.
  if (!order || typeof order.room !== 'string' || !/^[a-f0-9]{24}$/.test(order.room)
    || !Number.isSafeInteger(order.sequence) || order.sequence <= 0
    || !Number.isSafeInteger(order.connection) || order.connection < 0) {
    props[`${PREFIX}${group}-evidence`] = 'invalid-order';
    return props;
  }
  const received = timestamp(evidence.sourceReceivedAt);
  const updated = timestamp(evidence.updatedAt);
  if (!received || !updated) {
    props[`${PREFIX}${group}-evidence`] = 'missing-time';
    return props;
  }
  // The Symbol does not contain the original field values. Even valid metadata
  // requires exact retained values + the visible button text/disabled comparison.
  // In particular, inherited metadata must never become a self-certified LOCK.
  props[`${PREFIX}${group}-evidence`] = 'retained-comparison-required';
  props[`${PREFIX}${group}-sequence`] = String(order.sequence);
  props[`${PREFIX}${group}-room`] = `sportsnew:list:live:${order.room}`;
  props[`${PREFIX}${group}-source-at`] = received;
  props[`${PREFIX}${group}-updated-at`] = updated;
  return props;
};

export function sportsnewMainOddsDomEvidence({ fixture, slot, enabled = false, mode = 'live' } = {}) {
  if (enabled !== true) return {};
  const base = { [`${PREFIX}schema`]: 'public-dom-evidence/v1' };
  if (mode !== 'live' || (fixture?.mode != null && fixture.mode !== 'live')
    || (fixture?.sourceMode != null && fixture.sourceMode !== 'live')) {
    return { ...base, [`${PREFIX}evidence`]: 'wrong-mode' };
  }
  const odd = SLOTS.has(slot) ? fixture?.mainOdds?.[slot] : null;
  const fixtureId = publicId(fixture?.fixtureId ?? fixture?.id);
  const oddId = publicId(odd?.oddStateId);
  if (!fixtureId || !oddId) return { ...base, [`${PREFIX}evidence`]: 'missing-identity' };
  const price = publicPrice(odd.priceDecimal);
  const bettable = typeof odd.bettable === 'boolean' ? String(odd.bettable) : null;
  if (price === null || bettable === null) return { ...base, [`${PREFIX}evidence`]: 'invalid-values' };
  const evidence = sportsnewDisplayOddGroupEvidence(odd);
  const priceOrder = evidence?.price?.order;
  const availabilityOrder = evidence?.availability?.order;
  const mixedOwner = priceOrder && availabilityOrder && (
    priceOrder.room !== availabilityOrder.room || priceOrder.connection !== availabilityOrder.connection
  );
  const unavailable = mixedOwner ? 'mixed-owner' : derivedAvailability(odd);
  return {
    ...base,
    [`${PREFIX}evidence`]: 'observed-values-only',
    [`${PREFIX}mode`]: 'live',
    [`${PREFIX}fixture-id`]: fixtureId,
    [`${PREFIX}odd-id`]: oddId,
    [`${PREFIX}slot`]: slot,
    [`${PREFIX}price`]: price,
    [`${PREFIX}bettable`]: bettable,
    ...groupProps('price', evidence?.price, mixedOwner ? 'mixed-owner' : null),
    ...groupProps('availability', evidence?.availability, unavailable),
  };
}
